import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './MonthPicker.css';

const MOBILE_MQ = '(max-width: 768px)';

const MonthPicker = ({ selectedDate, onDateChange, label, maxDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(selectedDate.getFullYear());
  const [isMobileSheet, setIsMobileSheet] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  const pickerRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobileSheet(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (pickerRef.current?.contains(target)) return;
      if (overlayRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMobileSheet) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isMobileSheet]);

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const formatMonthDisplay = (date) => {
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const maxYear = maxDate ? maxDate.getFullYear() : null;
  const maxMonth = maxDate ? maxDate.getMonth() : null;

  const isMonthDisabled = (year, monthIndex) => {
    if (maxYear == null || maxMonth == null) return false;
    return year > maxYear || (year === maxYear && monthIndex > maxMonth);
  };

  const canGoNextYear = maxYear == null || currentYear < maxYear;

  const selectMonth = (monthIndex) => {
    if (isMonthDisabled(currentYear, monthIndex)) return;
    const newDate = new Date(currentYear, monthIndex, 1);
    onDateChange(newDate);
    setIsOpen(false);
  };

  const prevYear = () => {
    setCurrentYear(prev => prev - 1);
  };

  const nextYear = () => {
    if (!canGoNextYear) return;
    setCurrentYear(prev => prev + 1);
  };

  useEffect(() => {
    if (maxYear != null && currentYear > maxYear) {
      setCurrentYear(maxYear);
    }
  }, [maxYear, currentYear]);

  const currentMonth = selectedDate.getMonth();
  const currentYearValue = selectedDate.getFullYear();

  const overlay = isOpen ? (
    <div ref={overlayRef} className="month-picker-overlay-root">
      {isMobileSheet && (
        <button
          type="button"
          className="month-picker-backdrop"
          aria-label="Закрыть выбор месяца"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={
          isMobileSheet
            ? 'month-picker-dropdown month-picker-dropdown--sheet'
            : 'month-picker-dropdown month-picker-dropdown--anchored'
        }
        role="dialog"
        aria-modal={isMobileSheet ? 'true' : undefined}
        aria-label="Выбор месяца"
      >
        <div className="month-picker-header">
          <button type="button" className="month-picker-nav-button" onClick={prevYear}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <div className="month-picker-year">{currentYear}</div>
          <button
            type="button"
            className="month-picker-nav-button"
            onClick={nextYear}
            disabled={!canGoNextYear}
            aria-label="Следующий год"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <div className="month-picker-grid">
            {monthNames.map((month, index) => {
              const disabled = isMonthDisabled(currentYear, index);
              return (
                <button
                  key={index}
                  type="button"
                  className={`month-picker-month ${currentMonth === index && currentYearValue === currentYear ? 'month-picker-month-selected' : ''} ${disabled ? 'month-picker-month--disabled' : ''}`}
                  onClick={() => selectMonth(index)}
                  disabled={disabled}
                >
                  {month}
                </button>
              );
            })}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="month-picker-container" ref={pickerRef}>
      <button
        type="button"
        className="month-picker-button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span>{label || formatMonthDisplay(selectedDate)}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && isMobileSheet && typeof document !== 'undefined'
        ? createPortal(overlay, document.body)
        : isOpen
          ? overlay
          : null}
    </div>
  );
};

export default MonthPicker;
